import {
  ComponentType,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type FormStructureWithFormula,
  walkSchema,
} from "common/services/formBuilderService/walkSchema";
import {
  type BuilderContext,
  DEFAULT_MODEL_ID,
  SUPPORTED_MODELS,
  type SupportedModelId,
  type ToolActivity,
} from "common/utils/chatAgent/types";
import { FormStructure } from "common/utils/formStructure";
import { colors, spacing } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { FlexRow } from "../../Common/__styles__/Layout";
import { fontSizes } from "../../Common/__styles__/Typography";
import {
  ChatContent,
  InputContainer,
  PanelContainer,
  ScrollableContent,
} from "./__styles__/ChatPanel";
import AssistantIntro, { AssistantSuggestion } from "./AssistantIntro";
import ChatHeader from "./ChatHeader";
import ChatHistory from "./ChatHistory";
import ChatInput from "./ChatInput";
import { useChatAgent } from "./hooks/useChatAgent";

export type ChatPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  submissionTypeVersionId?: string;
  // V1 payload (builderState)
  formData?: Record<string, any>;
  // V2 payload (FormStructure)
  formStructure?: FormStructure;
  onFormUpdate?: (updates: any) => void;
  api?: string;
  suggestions?: AssistantSuggestion[];
  // Show model picker (V2 only)
  showModelPicker?: boolean;
  ToolActivityCardComponent?: ComponentType<{ activity: ToolActivity }>;
  thinkingPhrases?: string[];
  workingPhrases?: string[];
  // V2: Context for server-side resolution
  builderContext?: BuilderContext;
  // Tracking props
  submissionTypeName?: string;
  submissionTypeCategory?: string;
  submissionId?: string;
  objectId?: string;
  objectType?: string;
};

function getLeafPaths(
  obj: Record<string, unknown>,
  prefix = "",
): Array<string> {
  const paths: Array<string> = [];
  for (const key of Object.keys(obj)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];
    if (value != null && typeof value === "object" && !Array.isArray(value)) {
      paths.push(...getLeafPaths(value as Record<string, unknown>, fullPath));
    } else {
      paths.push(fullPath);
    }
  }
  return paths;
}

const MODEL_OPTIONS = Object.values(SUPPORTED_MODELS);

type ModelSelectorProps = {
  value: SupportedModelId;
  onChange: (modelId: SupportedModelId) => void;
  disabled?: boolean;
};

const ModelSelector = ({ value, onChange, disabled }: ModelSelectorProps) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value as SupportedModelId)}
    disabled={disabled}
    style={{
      padding: "4px 8px",
      borderRadius: "4px",
      border: `1px solid ${colors.grey300.value}`,
      backgroundColor: disabled ? colors.bgUiList.value : colors.bgUiList.value,
      fontSize: fontSizes.body2,
      color: disabled
        ? colors.contentDisabled.value
        : colors.contentPrimary.value,
      cursor: disabled ? "not-allowed" : "pointer",
      outline: "none",
      opacity: disabled ? 0.7 : 1,
    }}
  >
    {MODEL_OPTIONS.map(model => (
      <option key={model.id} value={model.id}>
        {model.name}
      </option>
    ))}
  </select>
);

const ChatPanel = ({
  isOpen,
  onClose,
  submissionTypeVersionId,
  formData,
  formStructure,
  onFormUpdate,
  api,
  suggestions,
  showModelPicker = false,
  ToolActivityCardComponent,
  thinkingPhrases,
  workingPhrases,
  builderContext,
  submissionTypeName,
  submissionTypeCategory,
  submissionId,
  objectId,
  objectType,
}: ChatPanelProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedModel, setSelectedModel] =
    useState<SupportedModelId>(DEFAULT_MODEL_ID);

  const commonTrackingProps = useMemo(
    () => ({
      submissionTypeName,
      submissionTypeCategory,
      submissionTypeVersionId,
      submissionId,
      objectId,
      objectType,
    }),
    [
      submissionTypeName,
      submissionTypeCategory,
      submissionTypeVersionId,
      submissionId,
      objectId,
      objectType,
    ],
  );

  const trackAI = useCallback(
    (eventName: string, extra?: Record<string, unknown>) => {
      track(eventName, { ...commonTrackingProps, ...extra });
    },
    [commonTrackingProps],
  );

  // we are only sending submission type name from the individual submission, not from the submission builder
  const isSubmissionContext = !!submissionTypeName;

  const fieldTitlesByPath = useMemo(() => {
    if (!formStructure) return {};
    return walkSchema<Record<string, string>>({
      formStructure: formStructure as FormStructureWithFormula,
      visit: ({ path, titles, isLeaf }) => {
        if (!isLeaf) return {};
        const title = titles.filter(Boolean).join(" > ");
        return title ? { [path]: title } : {};
      },
      merge: (acc, result) => ({ ...acc, ...result }),
    });
  }, [formStructure]);

  const handleFormUpdateWithTracking = useCallback(
    (updates: any) => {
      const leafKeys = updates?.formData ? getLeafPaths(updates.formData) : [];
      const fieldsUpdated = leafKeys.length;
      if (fieldsUpdated > 0) {
        const fieldsUpdatedNames = leafKeys.map(
          (key: string) => fieldTitlesByPath[key] ?? key,
        );
        if (isSubmissionContext) {
          trackAI("AI Assistant form updated", {
            fieldsUpdated,
            fieldsUpdatedNames,
            source: "chat",
          });
        } else {
          track("Builder assistant form updated", {
            fieldsUpdated,
            model: selectedModel,
          });
        }
      }
      onFormUpdate?.(updates);
    },
    [
      trackAI,
      onFormUpdate,
      isSubmissionContext,
      selectedModel,
      fieldTitlesByPath,
    ],
  );

  const {
    messages,
    input,
    handleSubmit,
    isLoading,
    clearChat,
    setInput,
    sendMessage,
    files,
    addFiles,
    removeFile,
    audioRecordingStatus,
    recordingTime,
    isTranscribing,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
  } = useChatAgent({
    api,
    submissionTypeVersionId,
    formData,
    formStructure,
    onFormUpdate: handleFormUpdateWithTracking,
    model: showModelPicker ? selectedModel : undefined,
    builderContext,
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setInput(suggestion);
      void sendMessage();
    },
    [setInput, sendMessage],
  );

  // Track when panel opens
  useEffect(() => {
    if (isOpen) {
      if (isSubmissionContext) {
        trackAI("AI Assistant opened");
      } else {
        track("Builder assistant chat opened");
      }
    }
  }, [isOpen, isSubmissionContext, trackAI]);

  // Reset chat when panel closes
  useEffect(() => {
    if (!isOpen) {
      clearChat();
    }
  }, [isOpen, clearChat]);

  const handleMessageSubmit = useCallback(
    (e: FormEvent) => {
      if (isSubmissionContext) {
        trackAI("AI Assistant message sent");
      } else {
        track("Builder assistant message sent", { model: selectedModel });
      }
      handleSubmit(e);
    },
    [handleSubmit, trackAI, isSubmissionContext, selectedModel],
  );

  const handleModelChange = useCallback((modelId: SupportedModelId) => {
    track("Builder assistant model changed", { model: modelId });
    setSelectedModel(modelId);
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmissionContext) {
      trackAI("AI Assistant closed");
    } else {
      track("Builder assistant chat closed");
    }
    onClose();
  }, [trackAI, onClose, isSubmissionContext]);

  const handleStopRecording = useCallback(() => {
    if (isSubmissionContext) {
      trackAI("AI Assistant audio recorded");
    } else {
      track("Builder assistant audio recorded");
    }
    onStopRecording();
  }, [trackAI, onStopRecording, isSubmissionContext]);

  const handleFilesAdd = useCallback(
    (newFiles: File[]) => {
      if (isSubmissionContext) {
        trackAI("AI Assistant file added", { fileCount: newFiles.length });
      } else {
        track("Builder assistant file added", { fileCount: newFiles.length });
      }
      addFiles(newFiles);
    },
    [trackAI, addFiles, isSubmissionContext],
  );

  return (
    <PanelContainer isOpen={isOpen}>
      <ChatHeader onClose={handleClose} />
      <ChatContent>
        <ScrollableContent>
          <AssistantIntro
            onSuggestionClick={handleSuggestionClick}
            suggestions={suggestions}
          />
          {messages.length > 0 && (
            <ChatHistory
              messages={messages}
              isLoading={isLoading}
              ToolActivityCardComponent={ToolActivityCardComponent}
              thinkingPhrases={thinkingPhrases}
              workingPhrases={workingPhrases}
            />
          )}
          <div ref={messagesEndRef} />
        </ScrollableContent>
        <InputContainer>
          <ChatInput
            value={input}
            onChange={e => setInput(e.target.value)}
            onSubmit={handleMessageSubmit}
            disabled={isLoading}
            placeholder="Let's get started..."
            files={files}
            onFilesAdd={handleFilesAdd}
            onFileRemove={removeFile}
            audioRecordingStatus={audioRecordingStatus}
            recordingTime={recordingTime}
            isTranscribing={isTranscribing}
            onStartRecording={onStartRecording}
            onStopRecording={handleStopRecording}
            onCancelRecording={onCancelRecording}
          />
          {showModelPicker && (
            <FlexRow css={{ paddingTop: spacing.s.value }}>
              <ModelSelector
                value={selectedModel}
                onChange={handleModelChange}
                disabled={messages.length > 0}
              />
            </FlexRow>
          )}
        </InputContainer>
      </ChatContent>
    </PanelContainer>
  );
};

export default ChatPanel;
